/**
 * KAN-128: cookie / analytics consent state.
 *
 * The banner writes to `localStorage.cookieConsent` ('accepted' | 'rejected')
 * and `localStorage.cookieConsentGranular` (per-purpose JSON). PostHog init
 * gates on `hasAnalyticsConsent()`. A tiny pub/sub lets PostHog flip on
 * dynamically when the user accepts after first paint, without a reload.
 */

export type CookieConsentDecision = 'accepted' | 'rejected';

export interface GranularConsent {
  analytics: boolean;
  functional: boolean;
  performance: boolean;
}

const KEY_DECISION = 'cookieConsent';
const KEY_GRANULAR = 'cookieConsentGranular';

const DEFAULT_GRANULAR: GranularConsent = {
  analytics: false,
  functional: true,
  performance: false,
};

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getCookieConsent(): CookieConsentDecision | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  const v = ls.getItem(KEY_DECISION);
  return v === 'accepted' || v === 'rejected' ? v : null;
}

export function getGranularConsent(): GranularConsent {
  const ls = safeLocalStorage();
  if (!ls) return { ...DEFAULT_GRANULAR };
  try {
    const raw = ls.getItem(KEY_GRANULAR);
    if (!raw) return { ...DEFAULT_GRANULAR };
    const parsed = JSON.parse(raw);
    return {
      analytics: !!parsed?.analytics,
      functional: parsed?.functional !== false,
      performance: !!parsed?.performance,
    };
  } catch {
    return { ...DEFAULT_GRANULAR };
  }
}

export function hasAnalyticsConsent(): boolean {
  const decision = getCookieConsent();
  if (decision === 'rejected') return false;
  if (decision === 'accepted') return true;
  return false;
}

type Listener = () => void;
const listeners = new Set<Listener>();

export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emitChange(): void {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* swallow */
    }
  });
}

export function setCookieConsent(decision: CookieConsentDecision): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEY_DECISION, decision);
  if (decision === 'accepted') {
    setGranularConsent({ analytics: true, functional: true, performance: true });
  } else {
    setGranularConsent({ analytics: false, functional: true, performance: false });
  }
  emitChange();
}

export function setGranularConsent(consent: GranularConsent): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEY_GRANULAR, JSON.stringify(consent));
  const decision: CookieConsentDecision = consent.analytics ? 'accepted' : 'rejected';
  ls.setItem(KEY_DECISION, decision);
  emitChange();
}
